/* eslint-disable @typescript-eslint/no-explicit-any, react-native/no-inline-styles */

import React, { useMemo, useRef, useState, ReactElement } from 'react';
import styled from '@emotion/styled';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import FontAwesome from 'react-fontawesome';

import { OL_COLOUR_BLACK_55 } from '../../Style/colours';
import { Checkbox } from '../../Checkbox/component';
import { useClickOutside, useKeyPress } from '../../../hooks/events';
import { Button } from '../../Buttons/Button/Button';
import {
  SelectorWrapper,
  SelectorDropdown,
  SelectorDropdownFlexWrap,
  SelectorModal,
  SelectorSearchInput,
  SelectorModalContainer,
  SelectorItems,
  SelectorModalFooter,
  SelectorModalFooterText,
} from './styles';
import { InputLabel } from '../../InputLabel/component';

type LabelType = string | number | null;

const SelectorCheckboxItem = styled(Checkbox)({
  display: 'flex',
  alignItems: 'center',

  'input[type="checkbox"]': {
    transform: 'scale(1.1)',
    width: 20,
  },

  '@supports (zoom: 1.4)': {
    'input[type="checkbox"]': {
      transform: 'none',
      zoom: 1.4,
      width: 20,
    },
  },

  label: {
    marginTop: 3,
    marginBottom: 0,
    fontWeight: 400,
  },
});

const SelectorDescription = styled('span')({
  display: 'block',
  fontWeight: 'normal',
  color: 'rgba(0, 0, 0, 0.55)',
  marginTop: '-0.3em',
  marginBottom: '0.3em',
});

type CheckboxSelectorProps<Option extends Record<string, any>> = {
  error?: string;
  required?: boolean;
  label?: string;
  description?: string;
  options: Option[];
  selectedOptions: Record<string, boolean>;
  setSelectedOptions: (options: Record<string, boolean>) => void;
  optionKey: keyof Option;
  getLabel: keyof Option | ((option: Option) => LabelType);
  selectorPrompt: JSX.Element | string;
  placeholder?: string;
  itemPluralName?: string;
  maxItemsSelected?: number;
  OptionLabel?: (
    option: Option & {
      __selectorLabel: string;
      __selectorValue: string;
    }
  ) => ReactElement;
  OptionLabelSeparatorInDropdown?: JSX.Element | string;
  filterBy?: (
    searchString: string
  ) => (
    option: Option & {
      __selectorLabel: string;
      __selectorValue: string;
    }
  ) => boolean;
  onInputValueChange: (value: string) => void;
  onSave?: (selections: Record<string, boolean>) => void;
};

export function CheckboxSelector<Option>({
  error = '',
  required = false,
  label,
  description,
  options,
  selectedOptions,
  setSelectedOptions,
  optionKey,
  getLabel,
  selectorPrompt,
  placeholder,
  itemPluralName,
  maxItemsSelected,
  OptionLabel = (option) => <div>{option.__selectorLabel}</div>,
  OptionLabelSeparatorInDropdown = <div>,&nbsp;</div>,
  filterBy,
  onInputValueChange,
}: CheckboxSelectorProps<Option>): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const selectorRef = useRef(null);

  const saveSelections = (): void => {
    setIsExpanded(false);
    setSearch('');
  };
  useClickOutside(selectorRef, saveSelections);
  useKeyPress(/* 27 is the escape keyCode */ 27, saveSelections);

  const mappedOptions = useMemo(
    () =>
      options.map((option) => {
        const label =
          typeof getLabel === 'function' ? getLabel(option) : option[getLabel];
        const labelAsString = String(label);

        return {
          ...option,
          __selectorLabel: labelAsString,
          __selectorValue: String(option[optionKey]),
        };
      }),
    [options, search, getLabel, optionKey]
  );

  const filteredOptions = filterBy
    ? mappedOptions.filter(filterBy(search))
    : mappedOptions;

  const filteredOptionsSlicedForSelectAll = filteredOptions.slice(
    0,
    typeof maxItemsSelected === 'number'
      ? maxItemsSelected
      : filteredOptions.length
  );

  const isSelectAllCheckboxChecked = useMemo(
    () =>
      filteredOptionsSlicedForSelectAll.every(
        (opt) => opt && selectedOptions[opt.__selectorValue]
      ),
    [filteredOptionsSlicedForSelectAll, selectedOptions]
  );

  const selectedItemsCount = useMemo(
    () => Object.values(selectedOptions).filter(Boolean).length,
    [selectedOptions]
  );

  let content = selectorPrompt as ReactElement | ReactElement[];

  if (selectedItemsCount > 0) {
    const selectedOptionsArray = filteredOptions.filter(
      (option) => selectedOptions[option.__selectorValue]
    );
    content = selectedOptionsArray.map((option, idx) => (
      <>
        <OptionLabel {...option} />
        {idx < selectedOptionsArray.length - 1 &&
          OptionLabelSeparatorInDropdown}
      </>
    ));
  }

  const onValueChange = (value: string): void => {
    setSearch(value);
    if (onInputValueChange) onInputValueChange(value);
  };

  return (
    <SelectorWrapper>
      {label && (
        <InputLabel error={error} required={required}>
          {label}
        </InputLabel>
      )}
      {description && <SelectorDescription>{description}</SelectorDescription>}
      <SelectorDropdown onClick={() => setIsExpanded(!isExpanded)}>
        <SelectorDropdownFlexWrap>{content}</SelectorDropdownFlexWrap>
        <FontAwesome name="caret-down" color={OL_COLOUR_BLACK_55} />
      </SelectorDropdown>

      {isExpanded && (
        <SelectorModal ref={selectorRef}>
          <SelectorModalContainer>
            <SelectorSearchInput
              className="textfield"
              value={search}
              onChange={onValueChange}
              placeholder={placeholder}
              icon="search"
            />

            <SelectorItems>
              {maxItemsSelected !== 0 && maxItemsSelected !== 1 && (
                <SelectorCheckboxItem
                  name="checkbox-selector-select-all"
                  label={
                    typeof maxItemsSelected === 'number'
                      ? `Select ${Math.min(
                          filteredOptions.length,
                          maxItemsSelected || 9999
                        )} ${itemPluralName || 'items'} ${
                          search ? '(filtered)' : ''
                        }`
                      : 'Select All'
                  }
                  checked={isSelectAllCheckboxChecked}
                  onChange={(isChecked) => {
                    setSelectedOptions(
                      mapValues(
                        keyBy(filteredOptionsSlicedForSelectAll, optionKey),
                        () => isChecked
                      )
                    );
                  }}
                />
              )}
              <div style={{ height: 6 }} />
              {filteredOptions.map((option) => {
                const isChecked =
                  selectedOptions[option.__selectorValue] || false;

                return (
                  <SelectorCheckboxItem
                    name="checkbox-selector"
                    label={<OptionLabel {...option} />}
                    checked={isChecked}
                    disabled={
                      typeof maxItemsSelected === 'number' &&
                      selectedItemsCount >= maxItemsSelected &&
                      !isChecked
                    }
                    onChange={(isChecked) =>
                      setSelectedOptions({
                        ...selectedOptions,
                        [option.__selectorValue]: isChecked,
                      })
                    }
                  />
                );
              })}
            </SelectorItems>
          </SelectorModalContainer>

          <SelectorModalFooter>
            <Button label="Save" colour="kelp" onClick={saveSelections} />
            {selectedItemsCount > 0 && (
              <SelectorModalFooterText>
                {selectedItemsCount} {`${itemPluralName || 'items'}`} selected
              </SelectorModalFooterText>
            )}
          </SelectorModalFooter>
        </SelectorModal>
      )}
    </SelectorWrapper>
  );
}
